import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEnvelopeOpenText } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Modal, Loading } from "../atoms";
import {
  startLoading,
  stopLoadingfn,
} from "../../state/actions/genericActions";
import { getLoading } from "../../state/selectors/general.selectors";
import apiClient from "../../services/apiClient";
import VerificationNotice from "./VerificationNotice";
import { cadetBlue } from "../../common/helpers/colors";

const EmailVerification = ({ userEmail, userName, varsomeHost, loading }) => {
  const [state, setState] = useState({
    isModalOpen: false,
    successMessage: "",
    verificationSent: false,
    error: false,
    errorText: "Something went wrong. Please try again later",
  });
  const helperTextStyle = { marginBottom: `10px` };
  const responses = {
    "Verification email has already been sent.":
      "Verification link has already been sent. If you have not received your email, please check out your spam inbox",
    "Verification email sent":
      "Verification link has been sent. If you have not received your email, please check out your spam inbox",
  };

  const { varsomeConfig } = window;
  const VarSomeLogoUrl = `${varsomeConfig.static_url}img/VarSomeLogo.png`;

  useEffect(() => {
    stopLoadingfn();
  }, []);

  const closeModal = () => setState({ ...state, isModalOpen: false });
  const openModal = () => setState({ ...state, isModalOpen: true });

  const handleOpenModal = () => {
    stopLoadingfn();
    const newState = { ...state };
    // If a success message already exists prevent new network requests
    if (!state.successMessage) {
      apiClient.sendEmailVerification().subscribe({
        next: (res) => {
          setState({
            ...newState,
            successMessage: responses[res.success],
            isModalOpen: true,
          });
        },
        error: () => {
          setState({ ...newState, error: true });
        },
      });
    }
    if (state.successMessage) openModal();
  };

  const handleResend = () => {
    startLoading();
    if (state.verificationSent) {
      stopLoadingfn();
      return;
    }
    const newState = { ...state };
    apiClient.reSendEmailVerification().subscribe({
      complete: () => {
        setState({ ...newState, verificationSent: true });
        stopLoadingfn();
      },
      next: () => {},
      error: () => {
        setState({ ...newState, error: true });
      },
    });
  };

  return (
    <div>
      {state.error && (
        <>
          <div className="container-fluid">
            <div className="varsome-request-container varsome-clearfix">
              <div className="varsome-request-error" id="varsome-request-error">
                {state.errorText}
              </div>
            </div>
          </div>
        </>
      )}
      {!state.error && (
        <>
          <div className="container-fluid varsome-results">
            <div
              className="varsome-request-info"
              style={state.successMessage ? helperTextStyle : null}
            >
              <h3>
                <b>Hi {userName},</b>
              </h3>
              <div className="tw-mt-4">
                <p>
                  In order to start or continue using VarSome, please verify
                  your e-mail address. You can change your account settings
                  including your e-mail address through the{" "}
                  <a href={`${varsomeHost}/account-settings`}>
                    account settings
                  </a>{" "}
                  page. If additional help is needed, you can always email us at{" "}
                  <a
                    href={`mailto:support@varsome.com?subject=Re: VarSome email verification for ${userEmail}`}
                  >
                    support@varsome.com
                  </a>{" "}
                  from the email address of this account.
                </p>
                <button
                  type="button"
                  className="tw-bg-green-500 tw-text-lg tw-h-12 tw-text-white tw-px-8"
                  onClick={handleOpenModal}
                  style={{ backgroundColor: cadetBlue }}
                >
                  Verify Your Email Address
                </button>
              </div>
            </div>

            <Modal
              isOpen={state.isModalOpen}
              onRequestClose={closeModal}
              ContentRender={() => (
                <div className="tw-max-w-3xl tw-w-90screen tw-p-6">
                  {state.verificationSent && !loading ? (
                    <>
                      <div>
                        <img
                          className="tw-object-contain tw-h-12 tw-w-full tw-object-left"
                          src={VarSomeLogoUrl}
                          alt="Varsome logo"
                        />
                      </div>
                      <h4 className="tw-text-center">
                        <b>Verification email sent</b>
                      </h4>
                      <p className="tw-text-center">
                        Verification e-mail has been sent to <b>{userEmail}</b>
                      </p>
                      <div className="tw-flex tw-flex-col tw-items-center tw-p-4">
                        <FontAwesomeIcon
                          icon={faCheck}
                          style={{ color: cadetBlue }}
                          size="7x"
                          className="tw-m-2"
                        />
                      </div>
                      <VerificationNotice shouldShow={state.verificationSent} />
                    </>
                  ) : null}
                  {loading ? (
                    <>
                      <div>
                        <img
                          className="tw-object-contain tw-h-12 tw-w-full tw-object-left"
                          src={VarSomeLogoUrl}
                          alt="Varsome logo"
                        />
                      </div>
                      <div className="tw-text-center">
                        <div className="tw-text-center">
                          <h4>Please wait</h4>
                        </div>
                        <Loading classNames="fa-10x" />
                      </div>
                    </>
                  ) : (
                    <>
                      {!state.verificationSent && (
                        <>
                          <div className="tw-items-start">
                            <img
                              className="tw-object-contain tw-h-12 tw-w-full tw-object-left"
                              src={VarSomeLogoUrl}
                              alt="Varsome logo"
                            />
                          </div>
                          <h4 className="tw-text-center">
                            <b>Verify your account</b>
                          </h4>
                          <p className="tw-text-center">
                            Account activation link has been sent to the e-mail
                            address that you provided
                          </p>
                          <div className="tw-flex tw-flex-col tw-items-center tw-p-8">
                            <FontAwesomeIcon
                              icon={faEnvelopeOpenText}
                              style={{ color: cadetBlue }}
                              size="7x"
                              className="tw-m-2"
                            />
                          </div>
                          {state.successMessage && (
                            <div
                              className="tw-items-center tw-text-center tw-content-center tw-text-sm tw-font-bold tw-px-4 tw-py-2"
                              role="alert"
                            >
                              {state.successMessage}
                            </div>
                          )}
                          <div className="tw-text-center tw-mt-1">
                            <a href="#" onClick={handleResend}>
                              Didn&apos;t get the email? Send again
                            </a>
                          </div>
                          <VerificationNotice shouldShow={true} />
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          </div>
          <div className="tw-w-1/3 tw-m-auto">
            <VerificationNotice shouldShow={state.successMessage} />
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: getLoading(state),
});

EmailVerification.propTypes = {
  userEmail: PropTypes.string,
  userName: PropTypes.string,
  varsomeHost: PropTypes.string,
  loading: PropTypes.bool,
};

EmailVerification.defaultProps = {
  userEmail: "",
  userName: "",
  varsomeHost: "",
  loading: false,
};

export default connect(mapStateToProps)(EmailVerification);
